import { FormControl, FormGroup } from '@angular/forms';
import {
  ConfigVoiceConfigInterface,
  DeepgramConfigInterface,
  ElevenLabsConfigInterface,
  ModelConfigInterface,
  OpenAIConfigInterface,
  OpenAIRealtimeConfigInterface,
  VendorModel,
} from '@vendasta/ai-assistants';
import { DEFAULT_PROVIDER_VOICES, DEFAULT_VOICE_PROVIDER, VoiceProviderFeatureFlags } from '../ai-assistant.constants';
import { AiVoiceFamily } from '../interfaces/config.interface';
import { familyFromVoice } from '../services/ai-assistant-utils';

export type DeepgramConfigFormControls = {
  voice: FormControl<string>;
};

export class DeepgramConfigForm extends FormGroup<DeepgramConfigFormControls> {
  constructor(deepgramConfig?: DeepgramConfigInterface) {
    super({
      voice: new FormControl(
        deepgramConfig?.voice ?? DEFAULT_PROVIDER_VOICES.get(VendorModel.VENDOR_MODEL_DEEPGRAM) ?? '',
        { nonNullable: true },
      ),
    });
  }
}

export type ElevenLabsConfigFormControls = {
  voice: FormControl<string>;
};

export class ElevenLabsConfigForm extends FormGroup<ElevenLabsConfigFormControls> {
  constructor(elevenLabsConfig?: ElevenLabsConfigInterface) {
    super({
      voice: new FormControl(
        elevenLabsConfig?.voice ?? DEFAULT_PROVIDER_VOICES.get(VendorModel.VENDOR_MODEL_ELEVEN_LABS) ?? '',
        { nonNullable: true },
      ),
    });
  }
}

export type OpenAIConfigFormControls = {
  voice: FormControl<string>;
};

export class OpenAIConfigForm extends FormGroup<OpenAIConfigFormControls> {
  constructor(openAIConfig?: OpenAIConfigInterface) {
    super({
      voice: new FormControl(
        openAIConfig?.voice ?? DEFAULT_PROVIDER_VOICES.get(VendorModel.VENDOR_MODEL_OPEN_AI)!,
        { nonNullable: true },
      ),
    });
  }
}

export type OpenAIRealtimeConfigFormControls = {
  voice: FormControl<string>;
  turnDetection: FormGroup<{
    threshold: FormControl<number | null>;
    prefixPadding: FormControl<number | null>;
    silenceDuration: FormControl<number | null>;
  }>;
};

export class OpenAIRealtimeConfigForm extends FormGroup<OpenAIRealtimeConfigFormControls> {
  constructor(openAIRealtimeConfig?: OpenAIRealtimeConfigInterface) {
    super({
      voice: new FormControl(
        openAIRealtimeConfig?.voice ?? DEFAULT_PROVIDER_VOICES.get(VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME) ?? '',
        { nonNullable: true },
      ),
      turnDetection: new FormGroup({
        threshold: new FormControl(openAIRealtimeConfig?.turnDetection?.threshold ?? 0.75),
        prefixPadding: new FormControl(openAIRealtimeConfig?.turnDetection?.prefixPadding ?? 300),
        silenceDuration: new FormControl(openAIRealtimeConfig?.turnDetection?.silenceDuration ?? 500),
      }),
    });
  }
}

export type ModelConfigFormControls = {
  openAIRealtimeConfig: OpenAIRealtimeConfigForm;
  openAIConfig: OpenAIConfigForm;
  deepgramConfig: DeepgramConfigForm;
  elevenLabsConfig: ElevenLabsConfigForm;
};

export class ModelConfigForm extends FormGroup<ModelConfigFormControls> {
  constructor(modelConfig?: ModelConfigInterface) {
    super({
      openAIRealtimeConfig: new OpenAIRealtimeConfigForm(modelConfig?.openaiRealtimeConfig),
      openAIConfig: new OpenAIConfigForm(modelConfig?.openaiConfig),
      deepgramConfig: new DeepgramConfigForm(modelConfig?.deepgramConfig),
      elevenLabsConfig: new ElevenLabsConfigForm(modelConfig?.elevenLabsConfig),
    });
  }
}

export type AiVoiceConfigFormControls = {
  vendorModel: FormControl<VendorModel | null>;
  voiceFamily: FormControl<AiVoiceFamily | null>;
  modelConfig: ModelConfigForm;
  preGreetingMessage: FormControl<string | null>;
  showPreGreetingMessage: FormControl<boolean>;
};

export class AiVoiceConfigurationForm extends FormGroup<AiVoiceConfigFormControls> {
  constructor(voiceConfig?: ConfigVoiceConfigInterface, voiceFeatures?: VoiceProviderFeatureFlags) {
    let voice = DEFAULT_PROVIDER_VOICES.get(VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME)!;
    switch (voiceConfig?.vendorModel) {
      case VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME:
        voice = voiceConfig?.modelConfig?.openaiRealtimeConfig?.voice ?? '';
        break;
      case VendorModel.VENDOR_MODEL_DEEPGRAM:
        voice = voiceConfig?.modelConfig?.deepgramConfig?.voice ?? '';
        break;
      case VendorModel.VENDOR_MODEL_ELEVEN_LABS:
        voice = voiceConfig?.modelConfig?.elevenLabsConfig?.voice ?? '';
        break;
      case VendorModel.VENDOR_MODEL_OPEN_AI:
        voice = voiceConfig?.modelConfig?.openaiConfig?.voice ?? '';
    }
    const voiceFamily = familyFromVoice(
      voice,
      voiceFeatures ?? {
        isElevenLabsEnabled: false,
        isOpenAITTSEnabled: false,
      },
      voiceConfig?.vendorModel,
    );
    super({
      vendorModel: new FormControl(voiceConfig?.vendorModel ?? DEFAULT_VOICE_PROVIDER, {
        nonNullable: true,
      }),
      voiceFamily: new FormControl(voiceFamily ?? null, { nonNullable: true }),
      modelConfig: new ModelConfigForm(voiceConfig?.modelConfig),
      preGreetingMessage: new FormControl(voiceConfig?.preGreetingMessage ?? null, { nonNullable: true }),
      showPreGreetingMessage: new FormControl(!!voiceConfig?.preGreetingMessage, { nonNullable: true }),
    });
  }

  toAssistantVoiceConfig(): ConfigVoiceConfigInterface {
    return {
      vendorModel: this.controls?.vendorModel.value ?? undefined,
      modelConfig: this.buildVoiceModelConfig(),
      preGreetingMessage: this.controls?.showPreGreetingMessage.value ? this.controls?.preGreetingMessage.value ?? undefined : undefined,
    };
  }

  private buildVoiceModelConfig(): ModelConfigInterface {
    const family = this.controls?.voiceFamily?.value;
    const deepgramConfig = this.controls?.modelConfig?.controls?.deepgramConfig?.getRawValue();
    const elevenLabsConfig = this.controls?.modelConfig?.controls?.elevenLabsConfig?.getRawValue();
    const openaiRealtimeConfig = this.controls?.modelConfig?.controls?.openAIRealtimeConfig?.getRawValue();
    const openaiConfig = this.controls?.modelConfig?.controls?.openAIConfig?.getRawValue();

    if (family?.provider === VendorModel.VENDOR_MODEL_DEEPGRAM) {
      return {
        deepgramConfig: {
          voice: deepgramConfig?.voice,
        },
      };
    }

    if (family?.provider === VendorModel.VENDOR_MODEL_ELEVEN_LABS) {
      return {
        elevenLabsConfig: {
          voice: elevenLabsConfig?.voice,
        },
      };
    }

    if (family?.provider === VendorModel.VENDOR_MODEL_OPEN_AI) {
      return {
        openaiConfig: {
          voice: openaiConfig?.voice,
        },
      };
    }

    return {
      openaiRealtimeConfig: {
        voice: openaiRealtimeConfig?.voice,
        turnDetection: {
          threshold: openaiRealtimeConfig?.turnDetection.threshold ?? 0.75,
          prefixPadding: openaiRealtimeConfig?.turnDetection?.prefixPadding ?? 300,
          silenceDuration: openaiRealtimeConfig?.turnDetection?.silenceDuration ?? 500,
        },
      },
    };
  }
}
